import { onMounted, onUnmounted, ref } from 'vue'
import type { AnyEventObject } from 'xstate'
import { useComments } from '@/composables/src/useComments'
import { addHoldReason, updateStatus } from '@/service/tickets'
import { useTicketStore } from '@/stores/tickets'

const isHoldReasonModalOpen = ref(false)
const holdReasonData = ref<{
  ticketId: string
  ticketNumber: string
  transitionId: string
  originalStatusRequest?: AnyEventObject
} | null>(null)

export const useHoldReasonModal = () => {
  const { addComment } = useComments()
  const ticketStore = useTicketStore()

  const openHoldReasonModal = (data: {
    ticketId: string
    ticketNumber: string
    transitionId: string
    originalStatusRequest?: any
  }) => {
    holdReasonData.value = data
    isHoldReasonModalOpen.value = true
  }

  const closeHoldReasonModal = () => {
    isHoldReasonModalOpen.value = false
    holdReasonData.value = null
  }

  const handleHoldReasonSubmitted = async (event: Event) => {
    const customEvent = event as CustomEvent
    const data = customEvent.detail

    if (!holdReasonData.value) {
      console.error('No hold reason data available')

      return
    }

    const savedHoldReasonData = { ...holdReasonData.value }

    try {
      const { onSuccess: onAddSuccess, onError: onAddError } = addHoldReason(data.ticketId, {
        holdReason: data.reason,
        notes: `Hold reason set via guard validation`,
      })

      onAddSuccess(async (responseData) => {
        const isInTicketView = window.location.pathname.includes('/tickets/') && !window.location.pathname.includes('/queue')

        if (isInTicketView) {
          const ticketEvent = new CustomEvent('ticket-hold-reason-processed', {
            detail: data,
          })

          window.dispatchEvent(ticketEvent)
        } else {
          await addComment(data.ticketId, data.reason, 'Internal')

          const ticket = ticketStore.tickets.find((t) => t.id === data.ticketId)

          if (ticket) {
            ticket.holdReason = data.reason
          }

          ticketStore.triggerTableUpdate()
        }

        if (savedHoldReasonData?.originalStatusRequest) {
          const originalRequest = savedHoldReasonData.originalStatusRequest
          const { onSuccess: onRetrySuccess, onError: onRetryError } = updateStatus(parseInt(data.ticketId, 10), {
            ...originalRequest,
            preserveHoldReason: true,
          })

          onRetrySuccess((retryData) => {
            const ticket = ticketStore.tickets.find((t) => t.id === data.ticketId)

            if (ticket && retryData.ticket) {
              ticketStore.updateTicket(retryData.ticket)
            }
          })

          onRetryError((retryError) => {
            console.error('❌ Failed to retry status change after adding hold reason:', retryError)
          })
        } else {
          const onHoldStatus = ticketStore.statuses.find((s) => s.name.toLowerCase().includes('hold'))

          if (onHoldStatus) {
            const fallbackRequest = {
              statusId: onHoldStatus.id,
              ticketId: data.ticketId,
              statusName: onHoldStatus.name,
              actualTime: new Date(),
              selectedReason: data.reason,
              preserveHoldReason: true,
            }

            const { onSuccess: onFallbackSuccess, onError: onFallbackError } = updateStatus(
              parseInt(data.ticketId, 10),
              fallbackRequest,
            )

            onFallbackSuccess((retryData) => {
              const ticket = ticketStore.tickets.find((t) => t.id === data.ticketId)

              if (ticket && retryData.ticket) {
                ticketStore.updateTicket(retryData.ticket)
              }
            })

            onFallbackError((retryError) => {
              console.error('❌ Fallback status change failed:', retryError)
            })
          }
        }
      })

      onAddError((error) => {
        console.error('Failed to add hold reason to ticket:', error)
      })
    } catch (error) {
      console.error('Failed to add hold reason comment:', error)
    }
  }

  onMounted(() => {
    window.addEventListener('hold-reason-submitted', handleHoldReasonSubmitted)
  })

  onUnmounted(() => {
    window.removeEventListener('hold-reason-submitted', handleHoldReasonSubmitted)
  })

  const changeStatusDirect = (ticketId: string, statusRequest: any) => {
    return updateStatus(parseInt(ticketId, 10), statusRequest)
  }

  return {
    isHoldReasonModalOpen,
    holdReasonData,
    openHoldReasonModal,
    closeHoldReasonModal,
    changeStatusDirect,
  }
}
